import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { assembleStatement, submitECF, agreeECF, revertECF, reopenECF } from '@/api/cashflows';
import { createLine, updateLine } from '@/api/classifiedLines';
import { listCfCodes, getMappingStatus } from '@/api/cfCodes';
import Button from '@/components/ui/Button';
import Badge, { statusVariant } from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import Spinner from '@/components/ui/Spinner';
import Money from '@/components/ui/Money';
import { penceToPoundString, poundStringToPence, formatPeriod, sectionLabel, statusLabel } from '@/utils/format';
import type { AssembledStatement, AssembledStatementLine } from '@/types';
import { useAuth } from '@/hooks/useAuth';

// ── Period navigation helpers ────────────────────────────────────────────────
function parsePeriod(period: string): { fyEnd: number; p: number } | null {
  const m = period.match(/^(\d{2})-P(\d{1,2})$/);
  if (!m) return null;
  return { fyEnd: parseInt(m[1], 10), p: parseInt(m[2], 10) };
}

function buildPeriod(fyEnd: number, p: number): string {
  return `${String(fyEnd).padStart(2, '0')}-P${p}`;
}

function prevPeriod(period: string): string {
  const parsed = parsePeriod(period);
  if (!parsed) return period;
  let { fyEnd, p } = parsed;
  p -= 1;
  if (p < 1) { p = 12; fyEnd -= 1; }
  return buildPeriod(fyEnd, p);
}

function nextPeriod(period: string): string {
  const parsed = parsePeriod(period);
  if (!parsed) return period;
  let { fyEnd, p } = parsed;
  p += 1;
  if (p > 12) { p = 1; fyEnd += 1; }
  return buildPeriod(fyEnd, p);
}

// ── Sticky reconciliation bar ────────────────────────────────────────────────
function actorLabel(actor: { name: string | null; email: string } | null | undefined): string {
  if (!actor) return 'Unknown user';
  return actor.name?.trim() || actor.email;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ReconciliationBar({ rec }: { rec: AssembledStatement['reconciliation'] }) {
  const balanced = rec.isBalanced;
  const cf9999Ok = rec.cf9999BalancePence === 0;

  return (
    <div className="sticky top-0 z-10 bg-white border border-gray-200 rounded-xl shadow-sm px-5 py-3 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
      <span className="font-semibold text-gray-600 uppercase tracking-wide text-xs">Reconciliation</span>

      <div className="flex items-center gap-2">
        <span className="text-gray-500">Unreconciled</span>
        <span className={clsx('font-mono font-bold tabular-nums', balanced ? 'text-green-700' : 'text-red-600')}>
          <Money pence={rec.differencePence} />
        </span>
        {balanced && (
          <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-gray-500">CF9999</span>
        <span className={clsx('font-mono font-bold tabular-nums', cf9999Ok ? 'text-green-700' : 'text-red-600')}>
          <Money pence={rec.cf9999BalancePence} />
        </span>
        {cf9999Ok && (
          <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>

      <div className="flex items-center gap-2 text-gray-400">
        <span>Net cash change</span>
        <span className="font-mono tabular-nums"><Money pence={rec.netChangeInCashPence} /></span>
      </div>

      <div className="flex items-center gap-2 text-gray-400">
        <span>BS movement</span>
        <span className="font-mono tabular-nums"><Money pence={rec.bsCashMovementPence} /></span>
      </div>
    </div>
  );
}

// ── Notes cell (inline, own debounced save) ──────────────────────────────────
interface NotesCellProps {
  line: AssembledStatementLine;
  disabled: boolean;
}

function NotesCell({ line, disabled }: NotesCellProps) {
  const [value, setValue] = useState(line.notes ?? '');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setValue(line.notes ?? ''); }, [line.notes]);

  const persist = useCallback(async (v: string) => {
    if (!line.classifiedLineId) return;
    try {
      await updateLine(line.classifiedLineId, {
        amountPence: line.amountPence,
        notes: v.trim() || undefined,
      });
    } catch {
      // best-effort — notes don't affect reconciliation
    }
  }, [line.classifiedLineId, line.amountPence]);

  const handleChange = (v: string) => {
    setValue(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void persist(v), 800);
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    void persist(value);
  };

  if (disabled) {
    return value
      ? <span className="block text-xs text-gray-400 italic truncate" title={value}>{value}</span>
      : null;
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled || !line.classifiedLineId}
      placeholder={line.classifiedLineId ? 'Add note…' : 'Enter amount first'}
      maxLength={500}
      className={clsx(
        'w-full rounded border px-2 py-1 text-xs placeholder-gray-300',
        'focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400',
        !line.classifiedLineId
          ? 'border-dashed border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
          : 'border-gray-200 text-gray-600',
      )}
      aria-label={`Note for ${line.label}`}
    />
  );
}

// ── Editable amount input — commits on blur / Enter (not while typing) ────────
interface AmountInputProps {
  line: AssembledStatementLine;
  entityId: string;
  period: string;
  cfCodeId: string;
  onSaved: () => void;
  disabled?: boolean;
}

function AmountInput({ line, entityId, period, cfCodeId, onSaved, disabled }: AmountInputProps) {
  const [value, setValue] = useState(() => penceToPoundString(line.amountPence));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(penceToPoundString(line.amountPence));
  }, [line.amountPence]);

  const save = useCallback(
    async (raw: string) => {
      const pence = poundStringToPence(raw);
      // Nothing to persist if the value is unchanged — avoids a redundant write
      // (and statement refetch) every time the field is tabbed through.
      if (pence === line.amountPence) {
        setError('');
        return;
      }
      setSaving(true);
      setError('');
      try {
        if (line.classifiedLineId) {
          await updateLine(line.classifiedLineId, { amountPence: pence });
        } else {
          await createLine(entityId, period, { cfCodeId, amountPence: pence });
        }
        onSaved();
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message ?? 'Save failed';
        setError(msg);
      } finally {
        setSaving(false);
      }
    },
    [line.classifiedLineId, line.amountPence, entityId, period, cfCodeId, onSaved],
  );

  // Only update local state while typing — no save until the field is committed.
  // Accept only what makes up a decimal amount: an optional leading minus and
  // digits with up to two decimal places. This blocks stray characters and,
  // combined with type="text", removes the number-stepper (wheel/arrow) behaviour
  // that could silently bump 0 → 0.01.
  const handleChange = (val: string) => {
    if (val !== '' && !/^-?\d*\.?\d{0,2}$/.test(val)) return;
    setValue(val);
  };

  // Commit when the user tabs or clicks away.
  const handleBlur = () => {
    void save(value);
  };

  // Enter commits and drops focus (which also triggers the blur save).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const isNegative = line.amountPence < 0;

  return (
    <div className="relative">
      <div className="relative flex items-center">
        <span className="pointer-events-none absolute left-2 text-gray-500 text-xs">£</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="0.00"
          className={clsx(
            'w-32 rounded border px-6 py-1.5 text-right text-sm font-mono tabular-nums',
            'focus:outline-none focus:ring-2 focus:ring-blue-400',
            disabled
              ? 'cursor-not-allowed bg-gray-50 border-gray-200 text-gray-500'
              : 'bg-blue-50 border-blue-300 text-gray-900 hover:border-blue-400',
            isNegative && !disabled ? 'text-red-700' : '',
            error ? 'border-red-400 bg-red-50' : '',
          )}
          aria-label={`Amount for ${line.label}`}
        />
        {saving && (
          <Spinner size="sm" className="absolute right-2" />
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function EntityCashFlowPage() {
  const { entityId, period: periodParam } = useParams<{ entityId: string; period: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [period, setPeriod] = useState(periodParam ?? '');
  const [actionError, setActionError] = useState('');
  // Which action is awaiting the "not fully reconciled" confirmation, if any.
  const [confirmAction, setConfirmAction] = useState<'submit' | 'agree' | null>(null);

  const { data: statement, isLoading, isError } = useQuery({
    queryKey: ['statement', entityId, period],
    queryFn: () => assembleStatement(entityId!, period),
    enabled: !!(entityId && period),
  });

  const { data: cfCodes } = useQuery({
    queryKey: ['cf-codes'],
    queryFn: () => listCfCodes(false),
  });

  // Codes missing from the Qlik report mapping — warn preparers at the point of entry.
  const { data: mapping } = useQuery({
    queryKey: ['cf-code-mapping-status'],
    queryFn: getMappingStatus,
    staleTime: 5 * 60_000,
  });
  const unmappedCodes = new Set((mapping?.unmapped ?? []).map((u) => u.code));

  const codeToId = new Map<string, string>();
  if (cfCodes) {
    for (const c of cfCodes) codeToId.set(c.code, c.id);
  }

  const invalidateStatement = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['statement', entityId, period] });
    void queryClient.invalidateQueries({ queryKey: ['validation-board'] });
  }, [queryClient, entityId, period]);

  const submitMutation = useMutation({
    mutationFn: () => submitECF(entityId!, period),
    onSuccess: () => invalidateStatement(),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Submit failed';
      setActionError(msg);
    },
  });

  const agreeMutation = useMutation({
    mutationFn: () => agreeECF(entityId!, period),
    onSuccess: () => invalidateStatement(),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Agree failed';
      setActionError(msg);
    },
  });

  const revertMutation = useMutation({
    mutationFn: () => revertECF(entityId!, period),
    onSuccess: () => invalidateStatement(),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Revert failed';
      setActionError(msg);
    },
  });

  const reopenMutation = useMutation({
    mutationFn: () => reopenECF(entityId!, period),
    onSuccess: () => invalidateStatement(),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Reopen failed';
      setActionError(msg);
    },
  });

  const navigatePeriod = (newPeriod: string) => {
    setPeriod(newPeriod);
    navigate(`/entities/${entityId}/cashflow/${newPeriod}`, { replace: true });
  };

  const ecf = statement?.entityCashFlow;
  const status = ecf?.status ?? 'NOT_STARTED';
  // Per-entity capabilities computed server-side from the user's assignments
  // (MH item 7). Super users are allowed while the statement is still loading.
  const isSuperUser = user?.role === 'ADMIN' || user?.role === 'GROUP_FINANCE';
  const canSubmit = statement?.permissions?.canPrepare ?? isSuperUser;
  const canAgree = statement?.permissions?.canReview ?? isSuperUser;

  const isLocked = status === 'SUBMITTED' || status === 'AGREED' || !canSubmit;

  // Warn before Submit/Agree when the statement is not fully reconciled
  // (difference outside tolerance, or CF9999 still carrying a balance).
  const rec = statement?.reconciliation;
  const unreconciled = !!rec && (!rec.isBalanced || rec.cf9999BalancePence !== 0);

  const handleSubmitClick = () => {
    setActionError('');
    if (unreconciled) setConfirmAction('submit');
    else submitMutation.mutate();
  };

  const handleAgreeClick = () => {
    setActionError('');
    if (unreconciled) setConfirmAction('agree');
    else agreeMutation.mutate();
  };

  const handleConfirm = () => {
    if (confirmAction === 'submit') submitMutation.mutate();
    if (confirmAction === 'agree') agreeMutation.mutate();
    setConfirmAction(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {statement?.entityCashFlow.entity?.name ?? 'Cash Flow'}
          </h1>
          <p className="text-sm text-gray-500">Entity cash flow statement</p>
          {(status === 'SUBMITTED' || status === 'AGREED') && ecf && (
            <p className="mt-1 text-xs text-gray-500">
              {ecf.submittedAt && (
                <span>
                  Submitted by <span className="font-medium text-gray-700">{actorLabel(ecf.submittedBy)}</span>{' '}
                  on {formatDate(ecf.submittedAt)}
                </span>
              )}
              {status === 'AGREED' && ecf.agreedAt && (
                <span>
                  {ecf.submittedAt ? ' · ' : ''}
                  Agreed by <span className="font-medium text-gray-700">{actorLabel(ecf.agreedBy)}</span>{' '}
                  on {formatDate(ecf.agreedAt)}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigatePeriod(prevPeriod(period))}
            className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50"
            aria-label="Previous period"
          >
            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="min-w-[130px] text-center text-sm font-semibold text-gray-900">
            {formatPeriod(period)}
          </span>
          <button
            onClick={() => navigatePeriod(nextPeriod(period))}
            className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50"
            aria-label="Next period"
          >
            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Status + actions */}
        <div className="flex items-center gap-3">
          <Badge variant={statusVariant(status)}>{statusLabel(status)}</Badge>

          {canSubmit && (status === 'IN_PROGRESS' || status === 'NOT_STARTED') && (
            <Button
              size="sm"
              loading={submitMutation.isPending}
              onClick={handleSubmitClick}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
              Submit
            </Button>
          )}

          {canAgree && status === 'SUBMITTED' && (
            <>
              <Button
                size="sm"
                variant="secondary"
                loading={revertMutation.isPending}
                onClick={() => { setActionError(''); revertMutation.mutate(); }}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                Revert
              </Button>
              <Button
                size="sm"
                loading={agreeMutation.isPending}
                onClick={handleAgreeClick}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Agree
              </Button>
            </>
          )}

          {canAgree && status === 'AGREED' && (
            <Button
              size="sm"
              variant="secondary"
              loading={reopenMutation.isPending}
              onClick={() => { setActionError(''); reopenMutation.mutate(); }}
              title="Reopen for editing — clears the agreement; must be re-submitted and re-agreed"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
              Reopen
            </Button>
          )}
        </div>
      </div>

      {actionError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {actionError}
        </div>
      )}

      {/* Sticky reconciliation bar — always visible while scrolling */}
      {statement && (
        <ReconciliationBar rec={statement.reconciliation} />
      )}

      {/* Main statement */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          Failed to load cash flow statement. The server may be unavailable.
        </div>
      ) : statement ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-x-auto">
          {/* Column headers */}
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[640px]">
            <span>Code / Description</span>
            <div className="flex items-center gap-3">
              <span className="w-36 text-left">Notes</span>
              <span className="w-8 text-center">Sign</span>
              <span className="w-32 text-right">Qlik</span>
              <span className="w-32 text-right text-blue-600">Entry ↓</span>
            </div>
          </div>

          {/* Sections */}
          {statement.sections.map((section) => (
            <div key={section.section} className="border-b border-gray-100 last:border-b-0">
              {/* Section header */}
              <div className="bg-gray-50 px-4 py-2">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  {sectionLabel(section.section)}
                </span>
              </div>

              {/* Lines */}
              {section.lines.map((line) => {
                const isDerived = line.lineType === 'DERIVED';
                const cfCodeId = codeToId.get(line.cfCode) ?? line.cfCodeId;
                                const isNegative = line.amountPence < 0;

                return (
                  <div
                    key={line.cfCode}
                    className={clsx(
                      'flex items-center justify-between px-4 py-2 text-sm min-w-[640px]',
                      isDerived ? 'bg-gray-50/50' : 'bg-white',
                    )}
                  >
                    {/* Left: code + label */}
                    <div className="flex items-center gap-2 min-w-0 flex-1 pr-4">
                      <span className="text-xs font-mono text-gray-600 w-14 shrink-0">
                        {line.cfCode}
                      </span>
                      <span className={clsx('truncate', isDerived ? 'text-gray-500' : 'text-gray-800')}>
                        {line.label}
                      </span>
                      {!isDerived && unmappedCodes.has(line.cfCode) && (
                        <span
                          title={`${line.cfCode} is not in the Qlik report mapping — figures entered here will not appear in Qlik until it is added`}
                          aria-label="Not in Qlik report mapping"
                          className="shrink-0"
                        >
                          <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </span>
                      )}
                      {isDerived && (
                        <span title="Derived from Qlik" aria-label="Derived from Qlik">
                          <svg className="h-3.5 w-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </span>
                      )}
                    </div>

                    {/* Right: notes + sign + qlik + entry */}
                    <div className="flex items-center gap-3 shrink-0">
                      {/* Notes column — classified lines only */}
                      <div className="w-36">
                        {!isDerived && (
                          <NotesCell line={line} disabled={isLocked} />
                        )}
                      </div>

                      {/* Sign */}
                      <span
                        className="w-8 text-center text-xs text-gray-500"
                        title={line.signConvention === 'POSITIVE' ? 'Cash inflow' : 'Cash outflow'}
                      >
                        {line.signConvention === 'POSITIVE' ? '+' : '−'}
                      </span>

                      {/* Qlik column — derived lines only */}
                      <div className="w-32 text-right">
                        {isDerived ? (
                          <span
                            className={clsx(
                              'font-mono text-sm tabular-nums',
                              isNegative ? 'text-red-700' : 'text-gray-900',
                            )}
                          >
                            <Money pence={line.amountPence} />
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs select-none">—</span>
                        )}
                      </div>

                      {/* Entry column — classified lines only */}
                      <div className="w-32">
                        {!isDerived ? (
                          <AmountInput
                            line={line}
                            entityId={entityId!}
                            period={period}
                            cfCodeId={cfCodeId}
                            onSaved={invalidateStatement}
                            disabled={isLocked}
                          />
                        ) : (
                          <span className="block w-32" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Section subtotal — aligned with Entry column */}
              <div className="flex items-center border-t border-gray-100 bg-gray-50 px-4 py-2 text-sm font-semibold min-w-[640px]">
                <span className="text-gray-700 flex-1">Section total</span>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-36" />
                  <div className="w-8" />
                  <div className="w-32" />
                  <span
                    className={clsx(
                      'w-32 text-right font-mono tabular-nums',
                      section.subtotalPence < 0 ? 'text-red-700' : 'text-gray-900',
                    )}
                  >
                    <Money pence={section.subtotalPence} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Confirmation when submitting/agreeing an unreconciled statement */}
      <Modal
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        title={confirmAction === 'agree' ? 'Agree without full reconciliation?' : 'Submit without full reconciliation?'}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleConfirm}>
              {confirmAction === 'agree' ? 'Agree anyway' : 'Submit anyway'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>This cash flow is not fully reconciled:</p>
          <ul className="list-disc space-y-1 pl-5">
            {rec && !rec.isBalanced && (
              <li>
                Difference of{' '}
                <span className="font-mono font-semibold text-red-700">
                  <Money pence={rec.differencePence} />
                </span>{' '}
                between net cash change and BS movement.
              </li>
            )}
            {rec && rec.cf9999BalancePence !== 0 && (
              <li>
                CF9999 (unclassified) balance of{' '}
                <span className="font-mono font-semibold text-red-700">
                  <Money pence={rec.cf9999BalancePence} />
                </span>
                .
              </li>
            )}
          </ul>
          <p>
            {confirmAction === 'agree'
              ? 'Are you sure you want to agree it as it stands?'
              : 'Are you sure you want to submit it as it stands?'}
          </p>
        </div>
      </Modal>
    </div>
  );
}
